/**
 * The default chart icon set.
 *
 * Superset's KPI strip uses emoji (⬇ 💰 ★ 💬 🏆 🌎). Emoji are free, but they
 * render differently on every platform and cannot take the chip's colour — the
 * icon ends up fighting the design rather than serving it. These are inline
 * SVGs on `currentColor`, so the tone class colours them.
 *
 * The important part for authoring: **an author who picks nothing still gets a
 * sensible icon and colour.** `inferIcon` matches the measure or label against
 * the names below, so "Total Revenue" gets the money bag in orange without
 * anyone choosing. Explicit beats inferred, and nothing is ever required.
 *
 * Icons are decorative — `aria-hidden`, with the tile's label carrying meaning.
 */

export type IconName =
  | 'downloads'
  | 'revenue'
  | 'rating'
  | 'reviews'
  | 'rank'
  | 'countries'
  | 'users'
  | 'growth'
  | 'time'
  | 'crashes'
  | 'apps'
  | 'publishers';

/**
 * Pale background + saturated foreground pairs, measured off Superset's strip.
 *
 * The list is the runtime value and the type derives from it, so a tone cannot
 * exist in one and not the other — the studio's colour picker and the validator
 * that narrows a stored string both read `TONES`.
 */
export const TONES = ['teal', 'orange', 'yellow', 'purple', 'blue', 'green', 'red'] as const;

export type Tone = (typeof TONES)[number];

const P: Record<IconName, string> = {
  // arrow into a tray — downloads/installs
  downloads: 'M12 3v10m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2',
  // money bag
  revenue: 'M9 3h6l-1.2 2.4A7 7 0 0119 12.5C19 17 15.9 20 12 20s-7-3-7-7.5a7 7 0 015.2-7.1L9 3zm3 6v7m-1.8-5.2h3.1a1.4 1.4 0 010 2.8h-2.6a1.4 1.4 0 000 2.8h3.1',
  // star
  rating: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z',
  // speech bubble
  reviews: 'M20 12a7 7 0 01-7 7H8l-4 3v-4.6A7 7 0 0111 5h2a7 7 0 017 7z',
  // trophy
  rank: 'M7 4h10v5a5 5 0 01-10 0V4zM7 6H4v1a4 4 0 004 4m9-5h3v1a4 4 0 01-4 4m-3 4v3m-4 2h8',
  // globe
  countries: 'M12 3a9 9 0 100 18 9 9 0 000-18zm0 0c2.6 2.3 4 5.5 4 9s-1.4 6.7-4 9c-2.6-2.3-4-5.5-4-9s1.4-6.7 4-9zM3.5 9h17m-17 6h17',
  users: 'M16 19v-1.5a4 4 0 00-4-4H7a4 4 0 00-4 4V19M9.5 9.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM21 19v-1.5a4 4 0 00-3-3.9M16 2.6a4 4 0 010 7.7',
  growth: 'M3 17l5.5-5.5 3.5 3.5L21 6m0 0h-5.5M21 6v5.5',
  time: 'M12 21a9 9 0 100-18 9 9 0 000 18zm0-14v5l3.5 2',
  crashes: 'M12 3.8L21 19H3L12 3.8zm0 5.4v4.6m0 2.6v.6',
  apps: 'M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z',
  publishers: 'M4 21V6a1 1 0 011-1h8a1 1 0 011 1v15M14 10h5a1 1 0 011 1v10M7 9h3m-3 4h3m-3 4h3m7-4h1m-1 4h1M2 21h20',
};

/** The tone an icon wears unless the author says otherwise. */
export const DEFAULT_TONE: Record<IconName, Tone> = {
  downloads: 'teal',
  revenue: 'orange',
  rating: 'yellow',
  reviews: 'purple',
  rank: 'blue',
  countries: 'green',
  users: 'blue',
  growth: 'green',
  time: 'purple',
  crashes: 'red',
  apps: 'teal',
  publishers: 'orange',
};

/**
 * Word → icon. Order matters: the first match wins, so the more specific
 * patterns sit above the looser ones ("uninstall" must not read as "install").
 */
const RULES: Array<[RegExp, IconName]> = [
  [/crash|anr|error|fail/i, 'crashes'],
  [/uninstall|download|install|acquisition/i, 'downloads'],
  [/revenue|spend|sales|arpu|purchase|price|cost|gross/i, 'revenue'],
  [/rating|stars|score/i, 'rating'],
  [/review|comment|sentiment|praise|complaint/i, 'reviews'],
  [/rank|position|placing|chart ?position|leaderboard/i, 'rank'],
  [/countr|region|market|availabilit|geo/i, 'countries'],
  [/publisher|developer|studio/i, 'publishers'],
  [/user|audience|dau|mau|device|visitor/i, 'users'],
  [/retention|session|duration|time|days/i, 'time'],
  [/app|title|portfolio/i, 'apps'],
  [/growth|trend|change|delta|movement/i, 'growth'],
];

/**
 * Every icon, in the order the studio offers them. Derived from the path map so
 * adding an icon adds it to the picker — there is no second list to forget.
 */
export const ICON_NAMES = Object.keys(P) as IconName[];

/** The icon a label suggests, or null when nothing fits — never a wrong guess. */
export const inferIcon = (...text: Array<string | undefined>): IconName | null => {
  const haystack = text.filter(Boolean).join(' ');
  if (!haystack.trim()) return null;
  for (const [pattern, name] of RULES) {
    if (pattern.test(haystack)) return name;
  }
  return null;
};

interface Props {
  name: IconName;
  /** Overrides the icon's default tone. */
  tone?: Tone;
  /** Chip size in px; the glyph scales with it. */
  size?: number;
}

/** The icon in its coloured chip — the whole visual unit, as Superset draws it. */
export const ChartIcon = ({ name, tone, size = 38 }: Props) => (
  <span
    className={`cicon cicon--${tone ?? DEFAULT_TONE[name]}`}
    style={{ width: size, height: size, borderRadius: Math.round(size * 0.29) }}
    aria-hidden="true"
  >
    <svg viewBox="0 0 24 24" width={Math.round(size * 0.5)} height={Math.round(size * 0.5)} focusable="false">
      <path
        d={P[name]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);
